// invalidateMoneyViews — the single list of caches that must be dropped after
// ANY write in the billing domain.
//
// "Billing domain" is wider than the money views themselves, because the
// operator's real workflow crosses them constantly: correct a contract, and the
// invoice, the debtor's Outstanding and the subsidy rail all move; add an extra,
// and the next invoice and the account statement move; apply a payment, and the
// invoice's paid status moves. Contracts, children, extras and invoices are the
// INPUTS to the money, so a change to any of them invalidates the money views —
// and a money write invalidates them back. invalidateQueries only refetches
// queries that are currently mounted, so a wide list costs a refetch of what is
// actually on screen and nothing at all for the rest.
//
// Why this exists: the debtor detail page shows the same money from five
// different queries (KPI tiles, account statement, balance charts, cash-in
// register, applications). Each mutation hook used to hand-pick which of them
// to invalidate, and the picks drifted — the ledger namespace ended up
// invalidated by nothing at all, so the account statement and the running
// balance chart served a stale snapshot for the full 5-minute staleTime after
// every posting. An operator would book a correction, watch the statement not
// change, and reasonably conclude the feature was broken.
//
// The rule is now: if a mutation moves money, it calls this. Over-invalidating
// costs one refetch; under-invalidating shows the operator a number that is a
// lie, which is far more expensive.
//
// invalidateQueries does hierarchical prefix matching, so each `.all` root
// covers every entry beneath it.
//
// CROSS-TAB: a QueryClient is per-tab, so this pass reaches only the tab that
// issued the write. An operator working a batch of corrections with the
// portfolio open in a second tab would leave that tab holding its pre-write
// snapshot. Each invalidation is therefore also posted on a BroadcastChannel;
// tabs of the same origin that have subscribed run the same pass locally. The
// receiving side never re-posts, so the message cannot ping-pong.

import type { QueryClient } from "@tanstack/react-query";

import { childKeys } from "@/hooks/useChildren";
import { contractKeys } from "@/hooks/useContracts";
import { extraDaycareKeys } from "@/hooks/useExtraDaycare";
import { invoiceRevisionKeys } from "@/hooks/billing/useInvoiceRevisions";
import { invoiceKeys } from "@/hooks/useInvoices";
import { contractVersionKeys } from "@/hooks/useVersionOperations";
import { annualConfirmationKeys } from "./useAnnualConfirmation";
import { childExtrasKeys } from "./useChildExtras";
import { correctionKeys } from "./useDebtorCorrections";
import { importBatchKeys } from "./useImportBatches";
import { orgPaymentRegisterKeys } from "./useOrgPaymentRegister";
import { debtorAccountKeys } from "./useDebtorAccountsList";
import { debtorLedgerKeys } from "./useDebtorLedger";
import { debtorMonthlySeriesKeys } from "./useDebtorMonthlySeries";
import { debtorPaymentKeys } from "./useDebtorPayments";
import { invoiceCorrectionKeys } from "./useDebtorInvoiceCorrections";
import { paymentApplicationsKeys } from "./usePaymentApplications";
import { reviewQueueKeys } from "./useReviewQueue";
import { subsidyChildTotalsKeys } from "./useSubsidyChildTotals";
import { subsidyLedgerKeys } from "./useSubsidyLedger";
import { subsidyMatchingKeys } from "./useSubsidyMatching";

// Every money namespace, in one place so the cancel pass and the invalidate
// pass can never drift apart.
//
// Built lazily rather than at module scope: reading `.all` off each factory
// during module evaluation makes this file explode if any of those modules is
// still initialising or has been partially mocked in a test. Deferring to call
// time costs nothing and removes the load-order hazard entirely.
function moneyKeys(): readonly (readonly unknown[])[] {
  return [
    debtorAccountKeys.all,
    debtorLedgerKeys.all,
    debtorPaymentKeys.all,
    paymentApplicationsKeys.all,
    invoiceKeys.all,
    correctionKeys.all,
    invoiceCorrectionKeys.all,
    debtorMonthlySeriesKeys.all,
    reviewQueueKeys.all,
    subsidyMatchingKeys.all,
    subsidyLedgerKeys.all,
    subsidyChildTotalsKeys.all,
    importBatchKeys.all,
    invoiceRevisionKeys.all,
    orgPaymentRegisterKeys.all,
    // The annual confirmation preview shows invoiced money (#1862).
    annualConfirmationKeys.all,
    // The billing INPUTS. contractKeys.all is ['contracts'], which by prefix
    // matching also covers subsidyAmendKeys (['contracts','subsidy-amend']).
    contractKeys.all,
    contractVersionKeys.all,
    childExtrasKeys.all,
    extraDaycareKeys.all,
    childKeys.all,
  ];
}

// One channel name for the whole app. Kept module-private: the only way to
// reach it is invalidateMoneyViews / subscribeToMoneyViewInvalidations, so a
// caller cannot post a message this module does not understand.
const MONEY_VIEWS_CHANNEL = "reconciliation-money-views";
const INVALIDATE_MESSAGE = "invalidate-money-views";

// A single lazily-created channel per tab, shared by the post and the receive
// side. That is deliberate: BroadcastChannel does not deliver a message back to
// the object that sent it, so one instance means a tab can never invalidate
// itself twice off its own write. `undefined` = not resolved yet, `null` = this
// environment has no BroadcastChannel (older Safari, SSR, some test runners) —
// in which case every broadcast path degrades to a no-op and the in-tab
// invalidation above still stands.
let moneyViewsChannel: BroadcastChannel | null | undefined;

function getMoneyViewsChannel(): BroadcastChannel | null {
  if (moneyViewsChannel !== undefined) return moneyViewsChannel;
  try {
    moneyViewsChannel =
      typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel(MONEY_VIEWS_CHANNEL);
    // Node's BroadcastChannel (test runners, any SSR pass) holds the event loop
    // open until it is closed; the DOM one has no unref and ignores this. A
    // channel nobody is waiting on must not keep a worker alive.
    (moneyViewsChannel as unknown as { unref?: () => void } | null)?.unref?.();
  } catch {
    // A channel we cannot construct must not take the write down with it.
    moneyViewsChannel = null;
  }
  return moneyViewsChannel;
}

/**
 * Subscribe this tab's QueryClient to money invalidations broadcast by others.
 *
 * Call once per tab (main.tsx, next to the QueryClient it is given). Returns an
 * unsubscribe. A no-op where BroadcastChannel is unavailable.
 */
export function subscribeToMoneyViewInvalidations(
  queryClient: QueryClient,
): () => void {
  const channel = getMoneyViewsChannel();
  if (!channel) return () => {};

  const onMessage = (event: MessageEvent) => {
    if (event.data !== INVALIDATE_MESSAGE) return;
    // `broadcast: false` is the loop guard: a receiving tab that re-posted
    // would bounce the message back to the sender and around every other tab
    // forever. Nothing is lost by staying quiet — the original post already
    // reached every tab on the channel.
    void invalidateMoneyViews(queryClient, { broadcast: false });
  };

  channel.addEventListener("message", onMessage);
  return () => channel.removeEventListener("message", onMessage);
}

export interface InvalidateMoneyViewsOptions {
  /**
   * Post the invalidation to the other tabs of this origin. Default true.
   * Set false on the receiving side of a broadcast (see the loop guard above)
   * and for a purely local refresh.
   */
  broadcast?: boolean;
}

/**
 * Invalidate every cache that can show a debtor's money.
 *
 * Call from the `onSuccess` of any mutation that writes payments, payment
 * applications, corrections, invoice overrides, or the credit pool.
 *
 * In-flight reads are CANCELLED before invalidating. This is not belt and
 * braces — it fixes a real intermittent bug. If a read left the browser before
 * the write committed, it is still in flight when `onSuccess` runs. TanStack
 * dedupes a refetch against an in-flight fetch, so the invalidation does not
 * start a second request; the older response then lands and populates the
 * cache with pre-write data, and the query is left merely "stale". Nothing
 * corrects that until the operator next focuses the tab — the money views now
 * refetch on focus, but a correction that silently reverts on screen until
 * someone alt-tabs is still a lie. Cancelling first guarantees the post-write
 * refetch is the one that wins.
 *
 * Returns a promise that resolves once the cancel pass AND every invalidation
 * has settled — `invalidateQueries` awaits the refetch of any active query, so
 * awaiting this genuinely means "the screen now shows the new numbers". Callers
 * that await it (mutation `onSuccess` handlers) therefore keep the mutation
 * pending until the money views are fresh, instead of resolving on a promise
 * that was quietly dropped.
 */
export async function invalidateMoneyViews(
  queryClient: QueryClient,
  { broadcast = true }: InvalidateMoneyViewsOptions = {},
): Promise<void> {
  await Promise.all(
    moneyKeys().map((queryKey) => queryClient.cancelQueries({ queryKey })),
  );
  await invalidateMoneyQueries(queryClient);
  if (broadcast) {
    // Posted after the local pass settles, and never awaited: another tab
    // being slow to refresh must not hold this tab's mutation pending.
    getMoneyViewsChannel()?.postMessage(INVALIDATE_MESSAGE);
  }
}

/** The invalidation pass on its own, without the cancel. */
async function invalidateMoneyQueries(queryClient: QueryClient): Promise<void> {
  // Collected and awaited together: dropping these promises is what let a
  // caller's `await invalidateMoneyViews()` resolve while the refetches were
  // still in flight.
  const pending: Promise<void>[] = [];
  // KPI tiles: Total billed / received / Outstanding / Available credit /
  // Account balance, plus the portfolio list rows.
  pending.push(
    queryClient.invalidateQueries({ queryKey: debtorAccountKeys.all }),
  );
  // Account statement + balance-bridge + running-balance charts all derive
  // from the full ledger feed.
  pending.push(
    queryClient.invalidateQueries({ queryKey: debtorLedgerKeys.all }),
  );
  // Cash-in register and the applications side-sheet.
  pending.push(
    queryClient.invalidateQueries({ queryKey: debtorPaymentKeys.all }),
  );
  pending.push(
    queryClient.invalidateQueries({ queryKey: paymentApplicationsKeys.all }),
  );
  // Invoice totals and statuses.
  pending.push(queryClient.invalidateQueries({ queryKey: invoiceKeys.all }));
  // Account-direct corrections and invoice-level corrections, both listed on
  // the debtor Extras tab.
  pending.push(queryClient.invalidateQueries({ queryKey: correctionKeys.all }));
  pending.push(
    queryClient.invalidateQueries({ queryKey: invoiceCorrectionKeys.all }),
  );
  // Per-debtor billed/collected series behind the list-row heatmaps.
  pending.push(
    queryClient.invalidateQueries({ queryKey: debtorMonthlySeriesKeys.all }),
  );
  // The inbound-payment queues: the parent Review queue and all three Subsidy
  // Matching surfaces (left rail, obligations grid, ledger, child totals).
  //
  // These used to be hand-picked in useSubsidyMatching's own mutations, which
  // is exactly the drift this module exists to stop. Two consequences: an
  // office-share CORRECTION never reached them at all, and a subsidy apply
  // reached them only in the tab that issued it — so an operator correcting an
  // invoice in a second browser tab came back to a rail still showing the
  // pre-correction Expected figure, with no signal that it was stale.
  pending.push(queryClient.invalidateQueries({ queryKey: reviewQueueKeys.all }));
  pending.push(
    queryClient.invalidateQueries({ queryKey: subsidyMatchingKeys.all }),
  );
  pending.push(
    queryClient.invalidateQueries({ queryKey: subsidyLedgerKeys.all }),
  );
  pending.push(
    queryClient.invalidateQueries({ queryKey: subsidyChildTotalsKeys.all }),
  );
  // Bank imports (a batch creates payments) and invoice revisions.
  pending.push(
    queryClient.invalidateQueries({ queryKey: importBatchKeys.all }),
  );
  pending.push(
    queryClient.invalidateQueries({ queryKey: invoiceRevisionKeys.all }),
  );
  // The org-wide cash-in register (Billing → Reconciliation → Cash-in).
  pending.push(
    queryClient.invalidateQueries({ queryKey: orgPaymentRegisterKeys.all }),
  );
  // The annual confirmation preview total (#1862): a correction moves it.
  pending.push(
    queryClient.invalidateQueries({ queryKey: annualConfirmationKeys.all }),
  );
  // The billing inputs. A contract, a child extra or an extra daycare day
  // decides what the next invoice bills; an invoice or payment write changes
  // what those surfaces report back (billed-to-date, paid status). The
  // relationship runs both ways, so one list serves both directions.
  pending.push(queryClient.invalidateQueries({ queryKey: contractKeys.all }));
  pending.push(
    queryClient.invalidateQueries({ queryKey: contractVersionKeys.all }),
  );
  pending.push(
    queryClient.invalidateQueries({ queryKey: childExtrasKeys.all }),
  );
  pending.push(
    queryClient.invalidateQueries({ queryKey: extraDaycareKeys.all }),
  );
  pending.push(queryClient.invalidateQueries({ queryKey: childKeys.all }));

  await Promise.all(pending);
}
